import { useState, type Ref } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useClientTranslation } from '@/lib/core/i18n';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore } from '@/lib/core/stores';
import { getFlowByServiceCategoryId } from '@/services/platform/data/booking';
import { cn } from '@/lib/core/utils/cn';
import type { LinkBlock } from './types';

interface BottomCtaBarProps {
  ref?: Ref<HTMLDivElement>;
  /** Positioning is the caller's — e.g. `coveredPanelClassName` from `explore/ui/covering-footer`. */
  className?: string;
  primaryCta?: LinkBlock | null;
  secondaryCta?: LinkBlock | null;
  serviceCategoryNodeKey?: string | null;
  venueNodeKey?: string | null;
  isShop?: boolean;
  primaryCtaDisabled?: boolean;
  secondaryCtaDisabled?: boolean;
  bookingDisabled?: boolean;
}

export function BottomCtaBar({
  ref,
  className,
  primaryCta,
  primaryCtaDisabled = false,
  secondaryCta,
  secondaryCtaDisabled = false,
  serviceCategoryNodeKey,
  venueNodeKey,
  isShop,
  bookingDisabled = false,
}: BottomCtaBarProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const { appSettings } = useApp();
  const locale = useLocaleStore(state => state.locale);
  const [isNavigating, setIsNavigating] = useState(false);

  // Shop shows both CTAs and no booking button; other categories show only the primary CTA + booking.
  const hasBooking = !isShop && !!serviceCategoryNodeKey;
  const showSecondaryCta = isShop && !!secondaryCta?.url;
  if (!primaryCta?.url && !showSecondaryCta && !hasBooking) return null;

  const handleBookNow = async () => {
    if (!serviceCategoryNodeKey || isNavigating || bookingDisabled) return;
    setIsNavigating(true);
    const flow = await getFlowByServiceCategoryId(appSettings, serviceCategoryNodeKey, locale);
    const routePath = flow?.route?.path;
    setIsNavigating(false);
    if (routePath) {
      navigate(routePath, { state: { categoryId: serviceCategoryNodeKey, venueNodeKey } });
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        'border-jncquoi-neutral-400 bg-jncquoi-neutral-00 border-t-[0.5px] px-4 pt-6 pb-10.5',
        className
      )}
    >
      <div className="flex flex-col gap-2">
        {primaryCta?.url && primaryCtaDisabled && (
          <Button
            type="button"
            variant="secondary-outline"
            palette="dark"
            size="md"
            disabled
            className="w-full min-w-0 px-0"
          >
            {primaryCta.name}
          </Button>
        )}
        {primaryCta?.url && !primaryCtaDisabled && (
          <Button
            asChild
            variant="secondary-outline"
            palette="dark"
            size="md"
            className="w-full min-w-0 px-0"
          >
            <a
              href={primaryCta.url}
              target={primaryCta.target ?? '_self'}
              rel={primaryCta.target === '_blank' ? 'noopener noreferrer' : undefined}
            >
              {primaryCta.name}
            </a>
          </Button>
        )}
        {showSecondaryCta && secondaryCtaDisabled && (
          <Button
            type="button"
            variant="primary-solid"
            palette="dark"
            size="md"
            disabled
            className="w-full min-w-0 px-0"
          >
            {secondaryCta!.name} T
          </Button>
        )}
        {showSecondaryCta && !secondaryCtaDisabled && (
          <Button asChild variant="primary-solid" palette="dark" size="md" className="w-full min-w-0 px-0">
            <a
              href={secondaryCta!.url!}
              target={secondaryCta!.target ?? '_self'}
              rel={secondaryCta!.target === '_blank' ? 'noopener noreferrer' : undefined}
            >
              {secondaryCta!.name}
            </a>
          </Button>
        )}
        {hasBooking && (
          <Button
            type="button"
            variant="primary-solid"
            palette="dark"
            size="md"
            disabled={isNavigating || bookingDisabled}
            onClick={handleBookNow}
            className="w-full min-w-0 px-0 disabled:opacity-60"
          >
            {t('App.Labels.BookNow')}
          </Button>
        )}
      </div>
    </div>
  );
}
